/**
 * Cycle 9 STORY-801: Per-product ROI calculator configurations.
 *
 * Each config defines:
 * - 2-3 input sliders (employees / volume / revenue) with reasonable ranges
 * - savings formula returning {annualSavings, paybackMonths}
 * - currency formatting (RUB)
 *
 * Baselines based on suite use cases (suites.ts) with "typical industry data"
 * disclaimer. Real customer cases will refine these post-pilot.
 */

export interface RoiInput {
  /** Field key */
  key: string;
  /** Display label */
  label: string;
  /** Optional unit (e.g. "сотрудников", "паллет/день") */
  unit?: string;
  /** Min / max / step for slider */
  min: number;
  max: number;
  step: number;
  /** Default value */
  defaultValue: number;
  /** Optional helper text */
  hint?: string;
}

export interface RoiResult {
  annualSavingsRub: number;
  paybackMonths: number;
  /** Human-readable formula explanation */
  formula: string;
  /** Key intermediate values for transparency */
  breakdown: { label: string; value: string }[];
}

export interface RoiConfig {
  productSlug: string;
  productLabel: string;
  /** 2-3 input fields */
  inputs: RoiInput[];
  /** Pure function: takes input values, returns savings result */
  calculate: (values: Record<string, number>) => RoiResult;
  /** Disclaimer + assumptions text */
  disclaimer: string;
}

const formatRub = (n: number): string => {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(n);
};

export const roiConfigs: RoiConfig[] = [
  {
    productSlug: "shift-brief-safety",
    productLabel: "shift-brief-safety",
    inputs: [
      {
        key: "employees",
        label: "Количество сотрудников",
        unit: "чел.",
        min: 100,
        max: 10000,
        step: 100,
        defaultValue: 1500,
      },
      {
        key: "trainingsPerYear",
        label: "Циклов обучения в год",
        unit: "циклов",
        min: 4,
        max: 24,
        step: 1,
        defaultValue: 12,
      },
      {
        key: "hourlyCost",
        label: "Средняя стоимость часа сотрудника",
        unit: "₽/час",
        min: 300,
        max: 3000,
        step: 50,
        defaultValue: 800,
      },
    ],
    calculate: (v) => {
      const employees = v.employees ?? 1500;
      const trainingsPerYear = v.trainingsPerYear ?? 12;
      const hourlyCost = v.hourlyCost ?? 800;
      const baselineHours = 4; // классический инструктаж
      const optimizedHours = 1.5; // shift-brief гейм-формат
      const hoursSaved = (baselineHours - optimizedHours) * trainingsPerYear * employees;
      const annualSavings = hoursSaved * hourlyCost;
      // Payback: assume license ~ 2.5M ₽/год
      const licenseCost = 2_500_000;
      const paybackMonths = annualSavings > 0 ? (licenseCost / annualSavings) * 12 : 99;
      return {
        annualSavingsRub: annualSavings,
        paybackMonths: Math.min(paybackMonths, 60),
        formula: "(4ч - 1.5ч) × циклов × сотрудников × стоимость_часа",
        breakdown: [
          { label: "Часов экономии в год", value: hoursSaved.toLocaleString("ru-RU") },
          { label: "Экономия", value: formatRub(annualSavings) },
        ],
      };
    },
    disclaimer:
      "Расчёт основан на типовых отраслевых данных: классический инструктаж занимает ~4 часа, гейм-формат shift-brief — ~1.5 часа. Реальная экономия зависит от структуры обучения вашего предприятия.",
  },
  {
    productSlug: "instruct-me",
    productLabel: "instruct-me",
    inputs: [
      {
        key: "employees",
        label: "Численность персонала",
        unit: "чел.",
        min: 50,
        max: 5000,
        step: 50,
        defaultValue: 800,
      },
      {
        key: "auditHoursPerMonth",
        label: "Часов на отчётность ОТ в месяц",
        unit: "часов",
        min: 10,
        max: 200,
        step: 5,
        defaultValue: 40,
      },
      {
        key: "specialistRate",
        label: "Стоимость часа специалиста ОТ",
        unit: "₽/час",
        min: 800,
        max: 5000,
        step: 100,
        defaultValue: 1500,
      },
    ],
    calculate: (v) => {
      const auditHoursPerMonth = v.auditHoursPerMonth ?? 40;
      const specialistRate = v.specialistRate ?? 1500;
      // Automation: 40h/month → 2h/month (95% reduction)
      const reductionPct = 0.95;
      const hoursSavedPerMonth = auditHoursPerMonth * reductionPct;
      const annualHoursSaved = hoursSavedPerMonth * 12;
      const annualSavings = annualHoursSaved * specialistRate;
      const licenseCost = 1_800_000;
      const paybackMonths = annualSavings > 0 ? (licenseCost / annualSavings) * 12 : 99;
      return {
        annualSavingsRub: annualSavings,
        paybackMonths: Math.min(paybackMonths, 60),
        formula: "часов_отчётности × 95% × 12 мес × ставка_специалиста",
        breakdown: [
          { label: "Часов специалиста экономии в год", value: annualHoursSaved.toLocaleString("ru-RU") },
          { label: "Экономия", value: formatRub(annualSavings) },
          { label: "Дополнительно: снижение штрафов Ростехнадзора", value: "+~500k ₽/год (среднее)" },
        ],
      };
    },
    disclaimer:
      "instruct-me автоматизирует ~95% работы по подготовке отчётности ОТ (классическая методика 40+ часов/месяц → ~2 часа). Числа — типовые для предприятий 500+ человек.",
  },
  {
    productSlug: "sales-planner",
    productLabel: "sales-planner",
    inputs: [
      {
        key: "monthlyRevenue",
        label: "Среднемесячная выручка",
        unit: "₽",
        min: 5_000_000,
        max: 500_000_000,
        step: 1_000_000,
        defaultValue: 50_000_000,
      },
      {
        key: "currentForecastError",
        label: "Текущая ошибка прогноза (MAPE)",
        unit: "%",
        min: 15,
        max: 50,
        step: 1,
        defaultValue: 30,
      },
    ],
    calculate: (v) => {
      const monthlyRevenue = v.monthlyRevenue ?? 50_000_000;
      const currentForecastError = v.currentForecastError ?? 30;
      // sales-planner: MAPE 15% → 12-18% (assume 14%)
      const optimizedError = 14;
      const errorReduction = Math.max(0, currentForecastError - optimizedError);
      // Каждый 1% reduction MAPE = ~0.3% savings of revenue (out-of-stock + overstock reduction)
      const savingsPctOfRevenue = errorReduction * 0.003;
      const annualRevenue = monthlyRevenue * 12;
      const annualSavings = annualRevenue * savingsPctOfRevenue;
      const licenseCost = 3_500_000;
      const paybackMonths = annualSavings > 0 ? (licenseCost / annualSavings) * 12 : 99;
      return {
        annualSavingsRub: annualSavings,
        paybackMonths: Math.min(paybackMonths, 60),
        formula: "(текущая_MAPE - 14%) × 0.3% × годовая_выручка",
        breakdown: [
          { label: "Снижение ошибки прогноза", value: `${errorReduction.toFixed(1)} п.п.` },
          { label: "Экономия (out-of-stock + overstock)", value: formatRub(annualSavings) },
        ],
      };
    },
    disclaimer:
      "sales-planner снижает MAPE до ~14%. Каждый 1 п.п. снижения экономит ~0.3% выручки за счёт out-of-stock + overstock reduction. Эмпирическая средняя по индустрии FMCG / B2B-дистрибуция.",
  },
  {
    productSlug: "palletix",
    productLabel: "palletix",
    inputs: [
      {
        key: "palletsPerDay",
        label: "Паллет в сутки",
        unit: "шт.",
        min: 50,
        max: 5000,
        step: 50,
        defaultValue: 300,
      },
      {
        key: "currentLoadPct",
        label: "Текущая загрузка паллеты",
        unit: "%",
        min: 60,
        max: 95,
        step: 1,
        defaultValue: 78,
      },
      {
        key: "logisticsCostPerPallet",
        label: "Логистика на паллету",
        unit: "₽",
        min: 200,
        max: 3000,
        step: 50,
        defaultValue: 800,
      },
    ],
    calculate: (v) => {
      const palletsPerDay = v.palletsPerDay ?? 300;
      const currentLoadPct = v.currentLoadPct ?? 78;
      const logisticsCostPerPallet = v.logisticsCostPerPallet ?? 800;
      // palletix: load +15-25% (assume +18%)
      const optimizedLoadPct = Math.min(95, currentLoadPct + 18);
      const palletReduction = 1 - currentLoadPct / optimizedLoadPct;
      const dailySavings = palletsPerDay * palletReduction * logisticsCostPerPallet;
      const annualSavings = dailySavings * 250; // ~250 working days
      const licenseCost = 1_200_000;
      const paybackMonths = annualSavings > 0 ? (licenseCost / annualSavings) * 12 : 99;
      return {
        annualSavingsRub: annualSavings,
        paybackMonths: Math.min(paybackMonths, 60),
        formula: "паллет/день × сокращение_паллет × стоимость × 250 дней",
        breakdown: [
          { label: "Новая загрузка", value: `${optimizedLoadPct.toFixed(0)}%` },
          { label: "Сокращение количества паллет", value: `${(palletReduction * 100).toFixed(1)}%` },
          { label: "Экономия логистики", value: formatRub(annualSavings) },
        ],
      };
    },
    disclaimer:
      "palletix оптимизирует загрузку паллет на 15-25% (среднее +18 п.п.). Экономия = меньше паллет × стоимость отгрузки. Зависит от вашей текущей загрузки — близкая к оптимуму даёт меньший прирост.",
  },
  {
    productSlug: "smart-pallet",
    productLabel: "smart-pallet",
    inputs: [
      {
        key: "planningHoursPerDay",
        label: "Часов на планирование паллет в день",
        unit: "часов",
        min: 1,
        max: 24,
        step: 0.5,
        defaultValue: 4,
      },
      {
        key: "logistRate",
        label: "Стоимость часа логиста",
        unit: "₽/час",
        min: 500,
        max: 3000,
        step: 100,
        defaultValue: 1200,
      },
    ],
    calculate: (v) => {
      const planningHoursPerDay = v.planningHoursPerDay ?? 4;
      const logistRate = v.logistRate ?? 1200;
      // smart-pallet: 4h → 30 min (87.5% reduction)
      const reductionPct = 0.875;
      const dailyHoursSaved = planningHoursPerDay * reductionPct;
      const annualSavings = dailyHoursSaved * logistRate * 250;
      const licenseCost = 600_000;
      const paybackMonths = annualSavings > 0 ? (licenseCost / annualSavings) * 12 : 99;
      return {
        annualSavingsRub: annualSavings,
        paybackMonths: Math.min(paybackMonths, 60),
        formula: "часов/день × 87.5% × ставка × 250 рабочих дней",
        breakdown: [
          { label: "Часов экономии в год", value: (dailyHoursSaved * 250).toLocaleString("ru-RU") },
          { label: "Экономия фонда логистов", value: formatRub(annualSavings) },
        ],
      };
    },
    disclaimer:
      "smart-pallet сокращает время планирования с типовых 4-6 часов на смену до ~30 минут. Числа — для одного логиста на одной площадке.",
  },
  {
    productSlug: "core-logic",
    productLabel: "core-logic",
    inputs: [
      {
        key: "monthlyOreVolume",
        label: "Объём руды в месяц",
        unit: "тонн",
        min: 10000,
        max: 1000000,
        step: 10000,
        defaultValue: 150000,
      },
      {
        key: "concentrateValuePerTon",
        label: "Стоимость концентрата за тонну",
        unit: "₽/т",
        min: 5000,
        max: 100000,
        step: 1000,
        defaultValue: 25000,
      },
    ],
    calculate: (v) => {
      const monthlyOreVolume = v.monthlyOreVolume ?? 150000;
      const concentrateValuePerTon = v.concentrateValuePerTon ?? 25000;
      // core-logic: качество концентрата +1.5% за счёт реал-тайм мониторинга
      const qualityImprovement = 0.015;
      const annualVolume = monthlyOreVolume * 12;
      const annualSavings = annualVolume * concentrateValuePerTon * qualityImprovement;
      const licenseCost = 4_500_000;
      const paybackMonths = annualSavings > 0 ? (licenseCost / annualSavings) * 12 : 99;
      return {
        annualSavingsRub: annualSavings,
        paybackMonths: Math.min(paybackMonths, 60),
        formula: "объём_руды × 12 мес × стоимость_концентрата × 1.5%",
        breakdown: [
          { label: "Прирост качества концентрата", value: "+1.5%" },
          { label: "Доп. выручка / снижение потерь", value: formatRub(annualSavings) },
        ],
      };
    },
    disclaimer:
      "core-logic даёт реал-тайм мониторинг гранулометрии и предсказывает отклонения. Эмпирический прирост качества — 1-2 п.п. в зависимости от стабильности процесса.",
  },
];

export function getRoiConfig(slug: string): RoiConfig | undefined {
  return roiConfigs.find((c) => c.productSlug === slug);
}

export const formatRubExport = formatRub;
